import { useState } from 'react';
import {
  Form,
  Icon,
  Input,
  Stack,
  Table,
  TableColumn,
  TableNoRecords,
} from '@servicetitan/design-system';
import { Alert, Text } from '@servicetitan/anvil2';
import { sanitizePhoneNumberTo10Digits } from '@lib/utils/formatPhone/sanitizePhoneNumberTo10Digits';
import { formatToNorthAmericanNumber } from '@lib/utils/formatPhone/formatToNorthAmericanNumber';
import { OutboundCallEntity } from '@lib/types/common-call-types';
import { mainLayoutLocators } from '@locators/main-layout';
import { OutboundCallDetails } from '@app/layouts/MainLayout/components/OutboundCall/OutboundCallModal/types';
import {
  useTechnicianTableState,
  TechnicianTableRow,
} from './useTechnicianTableState';
import strings from './strings';
import styles from './OutboundModalTechnicianTable.module.css';

interface TechnicianTableProps {
  setCallDetails: (details: OutboundCallDetails) => void;
}

const PhoneNumberCell: React.FC<{ phone: string }> = ({ phone }) => {
  if (!phone) {
    return <td>—</td>;
  }
  return (
    <td>{formatToNorthAmericanNumber(sanitizePhoneNumberTo10Digits(phone))}</td>
  );
};

const TechnicianRadioButtonCell: React.FC<{
  checked: boolean;
  row: TechnicianTableRow;
  onClickHandler: (row: TechnicianTableRow) => void;
}> = ({ checked, row, onClickHandler }) => (
  <td>
    <Form.Radio
      id={mainLayoutLocators.outboundAndTransferModal.technicianRowRadioBtn}
      checked={checked}
      label=''
      onClick={() => onClickHandler(row)}
      disabled={!row.phone}
      title={row.phone ? undefined : 'No phone number available'}
    />
  </td>
);

const getEmptyState = (allFailed: boolean, hasSearch: boolean) => {
  if (allFailed) return strings.error.allFailed;
  if (hasSearch) return strings.empty.noSearchResults;
  return strings.empty.noTechnicians;
};

const OutboundModalTechnicianTable: React.FC<TechnicianTableProps> = ({
  setCallDetails,
}) => {
  const state = useTechnicianTableState();
  const [partialAlertDismissed, setPartialAlertDismissed] = useState(false);
  const emptyState = getEmptyState(state.allFailed, !!state.search);

  return (
    <Stack direction='column' spacing={2}>
      <Stack.Item>
        <Input
          icon={{ name: 'search' }}
          iconPosition='left'
          className='w-100'
          size='small'
          placeholder={strings.search.placeholder}
          onChange={state.onSearchChange}
          value={state.search}
          disabled={state.allFailed}
        />
      </Stack.Item>

      {state.hasPartialError && !partialAlertDismissed && (
        <Stack.Item>
          <Alert
            status='warning'
            title={strings.error.partial.title}
            onClose={() => setPartialAlertDismissed(true)}
          >
            {strings.error.partial.body}
          </Alert>
        </Stack.Item>
      )}

      <Stack.Item>
        <Table
          selectable={{ enabled: true, mode: 'single' }}
          data={state.data}
          loading={state.loading}
          pageable={{ buttonCount: 3 }}
          pageSize={state.page.take}
          skip={state.page.skip}
          total={state.total}
          onPageChange={state.onPageChange}
          className={styles.TechnicianTable}
          rowHeight={40}
        >
          <TableNoRecords>
            {state.loading ? (
              <span />
            ) : (
              <div className={styles.EmptyState}>
                <Stack
                  direction='column'
                  spacing={0}
                  alignItems='center'
                  className='ta-center'
                >
                  <Icon
                    name='error_outline'
                    size={32}
                    color='#606162'
                    className={styles.EmptyStateIcon}
                  />
                  <Text size='small' style={{ fontWeight: 800 }}>
                    {emptyState.title}
                  </Text>
                  <Text variant='body' size='small' subdued>
                    {emptyState.body}
                  </Text>
                </Stack>
              </div>
            )}
          </TableNoRecords>
          <TableColumn
            width={40}
            cell={(props) => (
              <TechnicianRadioButtonCell
                checked={props.dataItem.id === state.selectedId}
                row={props.dataItem}
                onClickHandler={(row) => {
                  state.onSelectionChange(row.id);
                  setCallDetails({
                    number: sanitizePhoneNumberTo10Digits(row.phone),
                    callDisplayName: row.name,
                    entity: OutboundCallEntity.Technicians,
                    tenantId: row.tenantId,
                  });
                }}
              />
            )}
          />
          <TableColumn title={strings.header.name} field='name' />
          <TableColumn title={strings.header.tenant} field='tenantName' />
          <TableColumn
            title={strings.header.phoneNumber}
            field='phone'
            cell={(props) => <PhoneNumberCell phone={props.dataItem.phone} />}
          />
        </Table>
      </Stack.Item>
    </Stack>
  );
};

export default OutboundModalTechnicianTable;
